import { addToCart } from './cart.js?v=30';

/**
 * Parse the customs declaration payload emitted by c-size-drawer.liquid.
 * A malformed payload must never block add-to-cart, so it degrades to no properties.
 * @param {string|undefined} payload - JSON object of underscore-prefixed line item properties
 * @returns {object} Parsed properties, or an empty object
 */
const parseCustomsProperties = (payload) => {
  if (!payload) return {};

  try {
    return JSON.parse(payload);
  } catch (error) {
    console.error('Invalid customs properties payload:', error);
    return {};
  }
};

class SizeDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.panel = this.querySelector('[data-size-drawer-panel]');
    this.overlay = this.querySelector('[data-size-drawer-overlay]');
    this.closeButton = this.querySelector('[data-size-drawer-close]');
    this.sizeButtons = this.querySelectorAll('[data-size-option]');
    this.addToCartButton = this.querySelector('[data-size-drawer-add-to-cart]');
    this.addToCartText = this.querySelector('[data-add-to-cart-text]');
    this.notifyVariantInput = document.querySelector(
      '[data-notify-variant-input]'
    );
    this.isPreorder = this.querySelector('[data-preorder]');
    this.preOrderPlans = [];
    this.notifyMeSection = document.querySelector('[data-notify-me-section]');
    this.notifyMeButton = this.querySelector('[data-notify-me-button]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.selectedVariantId = null;
    this.selectedSoldOut = false;
    this.addingText = this.dataset.addingText;
    this.errorTryAgainText = this.dataset.errorTryAgainText;
    this.selectSizeText = this.dataset.selectSizeText;
    this.addToCartTextMessage = this.dataset.addToCartText;
    this.preorderMixedCartText = this.dataset.preorderMixedCartText;
    this.customsProperties = parseCustomsProperties(
      this.dataset.customsProperties
    );
    this.controller = new AbortController();
    this._preventScroll = (e) => {
      if (!this.panel.contains(e.target)) e.preventDefault();
    };
  }

  connectedCallback() {
    // Find trigger button(s) that open the drawer
    const triggers = document.querySelectorAll('[data-select-size-button]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(), {
        signal: this.controller.signal,
      });
    });

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close(), {
        signal: this.controller.signal,
      });
    }

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close(), {
        signal: this.controller.signal,
      });
    }

    // Size option buttons
    this.sizeButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectSize(button), {
        signal: this.controller.signal,
      });
    });

    // Add to cart button
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener('click', () => this.addToCart(), {
        signal: this.controller.signal,
      });
    }

    // Notify me button
    if (this.notifyMeButton) {
      this.notifyMeButton.addEventListener(
        'click',
        () => document.dispatchEvent(new CustomEvent('klaviyo-notify:open')),
        { signal: this.controller.signal }
      );
    }

    // ESC key to close
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      },
      { signal: this.controller.signal }
    );

    // listen to cart:error event
    document.addEventListener(
      'cart:error',
      (e) => {
        this.errorMessage.textContent = e.detail
          ? e.detail
          : this.errorTryAgainText;
        this.errorMessage.classList.remove('hidden');
        setTimeout(() => {
          this.errorMessage.classList.add('hidden');
        }, 3000);
        // set back the add to cart button text
        if (this.addToCartText) {
          this.addToCartText.textContent = this.addToCartTextMessage;
        }
        // set back the add to cart button disabled state
        if (this.addToCartButton) {
          this.addToCartButton.disabled = false;
        }
      },
      { signal: this.controller.signal }
    );
    // listen to cart:success event
    document.addEventListener(
      'cart:success',
      (e) => {
        this.close();
        // set back the add to cart button text
        if (this.addToCartText) {
          this.addToCartText.textContent = this.addToCartTextMessage;
        }
        // set back the add to cart button disabled state
        if (this.addToCartButton) {
          this.addToCartButton.disabled = false;
        }
      },
      { signal: this.controller.signal }
    );

    // listen to size-chart-toggle event
    document.addEventListener(
      'size-chart-toggle:open',
      (e) => {
        this.open();
      },
      { signal: this.controller.signal }
    );

    // check if open-size-selector is in sessionStorage
    if (sessionStorage.getItem('open-size-selector') === 'true') {
      sessionStorage.removeItem('open-size-selector');
      this.open();
    }
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  open() {
    this.isOpen = true;
    this.panel.classList.remove('translate-x-full');
    this.panel.classList.add('translate-x-0');
    this.panel.setAttribute('aria-hidden', 'false');

    if (this.overlay) {
      this.overlay.classList.remove('opacity-0', 'pointer-events-none');
      this.overlay.classList.add('opacity-100');
    }

    document.body.style.overflow = 'hidden';
    document.addEventListener('touchmove', this._preventScroll, {
      passive: false,
    });
  }

  close() {
    this.isOpen = false;
    this.panel.classList.remove('translate-x-0');
    this.panel.classList.add('translate-x-full');
    this.panel.setAttribute('aria-hidden', 'true');

    if (this.overlay) {
      this.overlay.classList.remove('opacity-100');
      this.overlay.classList.add('opacity-0', 'pointer-events-none');
    }

    document.body.style.overflow = '';
    document.removeEventListener('touchmove', this._preventScroll);
  }

  selectSize(button) {
    const isSoldOut = button.dataset.soldOut === 'true';

    // Remove active state and hide dot from all buttons
    this.sizeButtons.forEach((btn) => {
      btn.classList.remove('!border-black');
      const text = btn.querySelector('[data-size-text]');
      if (text) {
        text.classList.remove('after:opacity-100');
        text.classList.add('after:opacity-0');
      }
    });

    // Add active state to selected button and show dot
    button.classList.add('!border-black');
    const selectedText = button.querySelector('[data-size-text]');
    if (selectedText) {
      selectedText.classList.remove('after:opacity-0');
      selectedText.classList.add('after:opacity-100');
    }

    // Store selected variant ID and sold out state
    this.selectedVariantId = button.dataset.variantId;
    this.selectedSoldOut = isSoldOut;

    // Update notify form variant input
    if (this.notifyVariantInput) {
      this.notifyVariantInput.value = this.selectedVariantId;
    }

    // Toggle between add to cart and notify me
    if (isSoldOut) {
      // Hide add to cart, show notify me
      if (this.addToCartButton) {
        this.addToCartButton.classList.add('hidden');
      }
      if (this.notifyMeSection) {
        this.notifyMeSection.classList.remove('hidden');
        this.notifyMeSection.classList.add('flex');
      }
    } else {
      // Show add to cart, hide notify me
      if (this.addToCartButton) {
        this.addToCartButton.classList.remove('hidden');
        this.addToCartButton.disabled = false;
        if (this.addToCartText) {
          this.addToCartText.textContent = this.addToCartTextMessage;
        }
      }
      if (this.notifyMeSection) {
        this.notifyMeSection.classList.add('hidden');
        this.notifyMeSection.classList.remove('flex');
      }
    }
  }

  /**
   * Check whether adding an item would mix preorder and regular lines in the cart.
   * A preorder line is any line with a selling plan; gift wrapping counts as neither.
   * A failed cart fetch must never block add-to-cart, so it degrades to no conflict.
   * @param {boolean} isIncomingPreorder - Whether the item being added is a preorder
   * @returns {Promise<boolean>} True if the add would create a mixed cart
   */
  async wouldMixCart(isIncomingPreorder) {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js', {
        cache: 'no-cache',
      });
      if (!response.ok) return false;

      const cart = await response.json();
      return cart.items
        .filter((item) => !item.properties?._isGiftWrapping)
        .some(
          (item) => Boolean(item.selling_plan_allocation) !== isIncomingPreorder
        );
    } catch (error) {
      console.error('Cart check error:', error);
      return false;
    }
  }

  async addToCart() {
    if (!this.selectedVariantId || this.selectedSoldOut) return;

    try {
      this.addToCartButton.disabled = true;
      if (this.addToCartText) {
        this.addToCartText.textContent = this.addingText;
      }

      // check if the product is a preorder
      if (this.isPreorder) {
        const tempPlans = document.querySelectorAll('.prequik-option-checkbox');
        this.preOrderPlans = [];
        tempPlans.forEach((plan) => {
          this.preOrderPlans.push({
            name: plan.id,
            id: plan.value,
          });
        });
      }

      // A cart is either all preorder lines or all regular lines, never mixed
      if (await this.wouldMixCart(this.preOrderPlans.length > 0)) {
        document.dispatchEvent(
          new CustomEvent('cart:error', { detail: this.preorderMixedCartText })
        );
        return;
      }

      if (this.preOrderPlans.length > 0) {
        await addToCart([
          {
            id: Number(this.selectedVariantId),
            quantity: 1,
            selected_selling_plan: this.preOrderPlans[0].id,
            selling_plan: this.preOrderPlans[0].id,
            properties: {
              Type: 'Preorder',
              '_plan-name': this.preOrderPlans[0].name,
              ...this.customsProperties,
            },
          },
        ]);
      } else {
        await addToCart([
          {
            id: Number(this.selectedVariantId),
            quantity: 1,
            properties: {
              ...this.customsProperties,
            },
          },
        ]);
      }

      // error handling by addToCart function

      // Remove active state and hide dot from all buttons
      this.sizeButtons.forEach((btn) => {
        btn.classList.remove('!border-black');
        const text = btn.querySelector('[data-size-text]');
        if (text) {
          text.classList.remove('after:opacity-100');
          text.classList.add('after:opacity-0');
        }
      });
    } catch (error) {
      console.error('Add to cart error:', error);
      if (this.addToCartText) {
        this.addToCartText.textContent = this.errorTryAgainText;
      }
    } finally {
      setTimeout(() => {
        if (this.addToCartButton && !this.selectedSoldOut) {
          this.addToCartButton.disabled = !this.selectedVariantId;
          if (this.addToCartText) {
            this.addToCartText.textContent = this.selectedVariantId
              ? this.addToCartTextMessage
              : this.selectSizeText;
          }
        }
      }, 2000);
    }
  }
}

if (!customElements.get('size-drawer')) {
  customElements.define('size-drawer', SizeDrawer);
}
